import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

function Customers() {
  const isMobile = useIsMobile();
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ name: '', phone: '', email: '' });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const debounceRef = useRef(null);

  const fetchCustomers = useCallback(async (searchTerm = '') => {
    setLoading(true);
    try {
      const params = searchTerm ? { search: searchTerm } : {};
      const res = await api.get('/customers', { params });
      setCustomers(res.data.customers);
    } catch (err) {
      console.error('Failed to load customers:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  // Debounced search — waits 300ms after typing stops
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (value.length >= 2 || value.length === 0) {
        fetchCustomers(value);
      }
    }, 300);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const handleCreateCustomer = async () => {
    if (!newCustomer.name.trim()) return;
    setSaving(true);
    try {
      const res = await api.post('/customers', {
        name: newCustomer.name.trim(),
        phone: newCustomer.phone.trim() || null,
        email: newCustomer.email.trim() || null,
      });
      setShowNewForm(false);
      setNewCustomer({ name: '', phone: '', email: '' });
      navigate(`/customers/${res.data.customer.id}`);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to create customer');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>Customers</h1>
        <button className="btn btn-primary btn-sm" onClick={() => setShowNewForm(true)}>+ New Customer</button>
      </div>

      {showNewForm && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <h3 style={{ marginBottom: '1rem' }}>New Customer</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Name *</label>
              <input type="text" placeholder="Full name" value={newCustomer.name} onChange={e => setNewCustomer(p => ({ ...p, name: e.target.value }))} />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Phone</label>
              <input type="tel" placeholder="+852 9123 4567" value={newCustomer.phone} onChange={e => setNewCustomer(p => ({ ...p, phone: e.target.value }))} />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Email</label>
              <input type="email" placeholder="email@example.com" value={newCustomer.email} onChange={e => setNewCustomer(p => ({ ...p, email: e.target.value }))} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn btn-primary btn-sm" onClick={handleCreateCustomer} disabled={saving || !newCustomer.name.trim()}>{saving ? 'Saving...' : 'Create'}</button>
            <button className="btn btn-secondary btn-sm" onClick={() => { setShowNewForm(false); setNewCustomer({ name: '', phone: '', email: '' }); }}>Cancel</button>
          </div>
        </div>
      )}

      <div className="search-bar">
        <input
          type="text"
          placeholder="Search by name, phone, or email..."
          value={search}
          onChange={handleSearch}
        />
      </div>
      {loading ? (
        <div className="loading">Loading customers...</div>
      ) : customers.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>
          {search ? 'No customers found' : 'No customers yet'}
        </div>
      ) : isMobile ? (
        /* ===== MOBILE: Card list ===== */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {customers.map((c) => (
            <div
              key={c.id}
              onClick={() => navigate(`/customers/${c.id}`)}
              style={{
                background: '#fff',
                borderRadius: 8,
                padding: '12px 16px',
                border: '1px solid #e5e7eb',
                cursor: 'pointer',
                transition: 'background 0.15s'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <strong style={{ fontSize: '0.95rem' }}>{c.name}</strong>
                <span className="tag tag-blue" style={{ fontSize: '0.7rem' }}>{c.country}</span>
              </div>
              <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                {c.phone || c.email || 'No contact info'}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: 2 }}>
                Since {formatDate(c.created_at)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* ===== DESKTOP: Table ===== */
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Country</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((c) => (
                <tr key={c.id} onClick={() => navigate(`/customers/${c.id}`)}>
                  <td><strong>{c.name}</strong></td>
                  <td>{c.phone || '—'}</td>
                  <td>{c.email || '—'}</td>
                  <td><span className="tag tag-blue">{c.country}</span></td>
                  <td>{formatDate(c.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default Customers;